import React, { useMemo } from "react";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import Button from "@amzn/meridian/button";
import Checkbox from "@amzn/meridian/checkbox";
import Select, { SelectOption } from "@amzn/meridian/select";
import Modal, { ModalFooter } from "@amzn/meridian/modal";
import { AUDIT_FIELDS } from "./constants";
import { Incident } from "./types";

interface FilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  dateRange: any[];
  setDateRange: (range: any[]) => void;
  selectedFields: string[];
  setSelectedFields: (fields: string[]) => void;
  selectedAuditResults: string[];
  setSelectedAuditResults: (results: string[]) => void;
  selectedSiteTypes: string[];
  setSelectedSiteTypes: (siteTypes: string[]) => void;
  selectedSites: string[];
  setSelectedSites: (sites: string[]) => void;
  selectedBusinessUnits: string[];
  setSelectedBusinessUnits: (businessUnits: string[]) => void;
  selectedOrganizations: string[];
  setSelectedOrganizations: (organizations: string[]) => void;
  selectedScopes: string[];
  setSelectedScopes: (scopes: string[]) => void;
  incidents: Incident[];
  onReset: () => void;
}

const FilterModal: React.FC<FilterModalProps> = ({
  isOpen,
  onClose,
  selectedFields,
  setSelectedFields,
  selectedSiteTypes,
  setSelectedSiteTypes,
  selectedSites,
  setSelectedSites,
  selectedBusinessUnits,
  setSelectedBusinessUnits,
  selectedOrganizations,
  setSelectedOrganizations,
  selectedScopes,
  setSelectedScopes,
  incidents,
  onReset,
}) => {
  // Get unique values from incidents
  const uniqueSiteTypes = useMemo(() => {
    const siteTypes = new Set<string>();
    incidents.forEach((incident) => {
      if (incident.siteType) {
        siteTypes.add(incident.siteType);
      }
    });
    return Array.from(siteTypes).sort();
  }, [incidents]);

  const uniqueSites = useMemo(() => {
    const sites = new Set<string>();
    incidents.forEach((incident) => {
      if (incident.site) {
        sites.add(incident.site);
      }
    });
    return Array.from(sites).sort();
  }, [incidents]);

  const uniqueBusinessUnits = useMemo(() => {
    const businessUnits = new Set<string>();
    incidents.forEach((incident) => {
      if (incident.obr_bu) {
        businessUnits.add(incident.obr_bu);
      }
    });
    return Array.from(businessUnits).sort();
  }, [incidents]);

  const uniqueOrganizations = useMemo(() => {
    const organizations = new Set<string>();
    incidents.forEach((incident) => {
      if (incident.obr_org) {
        organizations.add(incident.obr_org);
      }
    });
    return Array.from(organizations).sort();
  }, [incidents]);

  const uniqueScopes = useMemo(() => {
    const scopes = new Set<string>();
    incidents.forEach((incident) => {
      if (incident.obr_scope) {
        scopes.add(incident.obr_scope);
      }
    });
    return Array.from(scopes).sort();
  }, [incidents]);
  const handleFieldToggle = (field: string) => {
    if (selectedFields.includes(field)) {
      setSelectedFields(selectedFields.filter((f) => f !== field));
    } else {
      setSelectedFields([...selectedFields, field]);
    }
  };

  return (
    <Modal
      title="Filter Data"
      open={isOpen}
      onClose={onClose}
      scrollContainer="viewport"
      closeLabel="Close"
      width={600}
    >
      <Column spacing={"400"}>
        <Column spacing="200">
          <Text>Fields with Defects</Text>
          <Text type="b100" color="secondary">
            Show only incidents that have defects in selected fields
          </Text>
          <Row spacing="300">
            {AUDIT_FIELDS.slice(0, 3).map((field) => (
              <Checkbox
                key={field}
                checked={selectedFields.includes(field)}
                onChange={() => handleFieldToggle(field)}
              >
                {field}
              </Checkbox>
            ))}
          </Row>
          <Row spacing="300">
            {AUDIT_FIELDS.slice(3, 6).map((field) => (
              <Checkbox
                key={field}
                checked={selectedFields.includes(field)}
                onChange={() => handleFieldToggle(field)}
              >
                {field}
              </Checkbox>
            ))}
          </Row>
        </Column>

        <Row spacing="300">
          <Column>
            <Select
              label="Site Type"
              value={selectedSiteTypes}
              onChange={setSelectedSiteTypes}
              placeholder="Select site types..."
              width={250}
            >
              {uniqueSiteTypes.map((siteType) => (
                <SelectOption key={siteType} value={siteType} label={siteType} />
              ))}
            </Select>
          </Column>
          <Column>
            <Select
              label="Site"
              value={selectedSites}
              onChange={setSelectedSites}
              placeholder="Select sites..."
              width={250}
            >
              {uniqueSites.map((site) => (
                <SelectOption key={site} value={site} label={site} />
              ))}
            </Select>
          </Column>
        </Row>

        {/* Business Unit, Organization and Scope fields moved to main filter row */}
      </Column>

      <ModalFooter>
        <Row alignmentHorizontal="end" widths="fit">
          <Button type="secondary" size="small" onClick={onReset}>
            Clear
          </Button>
          <Button size="small" onClick={onClose}>
            Apply
          </Button>
        </Row>
      </ModalFooter>
    </Modal>
  );
};

export default FilterModal;
